import { session } from "../auth/session.js";
import { requestSelection } from "../appFlow.js";
import { audio } from "../audio/audio.js";
import { getInvertMouse, setInvertMouse } from "../settings.js";
import { injectTheme } from "./theme.js";

/**
 * The in-world ESC menu: a pixel-styled popup with "Character Selection" and "Log Out"
 * (plus Resume). Opened from the world's Escape handler. Black/white/gray pixel look to
 * match the auth + character screens. The DOM is built lazily on first open and reused.
 */
const STYLE_ID = "ghostland-gamemenu-style";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Silkscreen:wght@400;700&display=swap');
#gl-menu-overlay {
  position: fixed; inset: 0; z-index: 60;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0, 0, 0, 0.72);
}
#gl-menu-overlay, #gl-menu-overlay * { font-family: 'Silkscreen', ui-monospace, monospace !important; }

#gl-menu-card {
  position: relative; width: 280px; padding: 30px 26px 26px;
  display: flex; flex-direction: column; gap: 12px;
  background: #0b0b0b; border: 3px solid #f4f4f4; border-radius: 0;
  box-shadow:
    inset 0 0 0 3px #000,
    inset 0 0 0 6px #2c2c2c,
    0 0 0 3px #000,
    10px 10px 0 0 rgba(0,0,0,.6);
}
.gl-menu-corner { position: absolute; width: 10px; height: 10px; background: #f4f4f4; box-shadow: 0 0 0 3px #000; }
.gl-menu-corner.tl { top: -7px; left: -7px; }
.gl-menu-corner.tr { top: -7px; right: -7px; }
.gl-menu-corner.bl { bottom: -7px; left: -7px; }
.gl-menu-corner.br { bottom: -7px; right: -7px; }

#gl-menu-card .gl-menu-title {
  text-align: center; font-size: 16px; letter-spacing: 0.22em; text-transform: uppercase;
  color: #f4f4f4; text-shadow: 0 2px 0 #000; margin-bottom: 8px;
}
.gl-menu-btn {
  width: 100%; padding: 13px 12px; cursor: pointer;
  background: #000; border: 2px solid #fff; border-radius: 0; color: #fff;
  font-size: 13px; letter-spacing: 0.12em; text-transform: uppercase;
  box-shadow: 4px 4px 0 0 #000;
}
.gl-menu-btn:hover { background: #fff; color: #000; }
.gl-menu-btn:active { transform: translate(2px, 2px); box-shadow: 2px 2px 0 0 #000; }
.gl-menu-btn.danger { border-color: #b86a6a; color: #d89a9a; }
.gl-menu-btn.danger:hover { background: #b84a4a; border-color: #b84a4a; color: #000; }
.gl-menu-settings { border-top: 2px solid #2c2c2c; margin-top: 4px; padding-top: 14px; }
.gl-menu-settings .lbl { display: flex; justify-content: space-between; font-size: 11px; letter-spacing: .14em; color: #cfcfcf; text-transform: uppercase; margin-bottom: 8px; }
.gl-menu-settings input[type=range] { width: 100%; accent-color: #f4f4f4; cursor: pointer; }
.gl-menu-settings .toggle { display: flex; justify-content: space-between; align-items: center; margin-top: 12px; font-size: 11px; letter-spacing: .14em; color: #cfcfcf; text-transform: uppercase; cursor: pointer; }
.gl-menu-settings .toggle input[type=checkbox] { width: 16px; height: 16px; accent-color: #f4f4f4; cursor: pointer; }
`;

function ensureStyle(): void {
  injectTheme();
  if (document.getElementById(STYLE_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}

let overlay: HTMLDivElement | null = null;

function build(): HTMLDivElement {
  ensureStyle();
  const el = document.createElement("div");
  el.id = "gl-menu-overlay";
  el.innerHTML = `
    <div id="gl-menu-card">
      <span class="gl-menu-corner tl"></span>
      <span class="gl-menu-corner tr"></span>
      <span class="gl-menu-corner bl"></span>
      <span class="gl-menu-corner br"></span>
      <div class="gl-menu-title">Settings</div>
      <button class="gl-menu-btn" data-act="resume" type="button">Resume</button>
      <button class="gl-menu-btn" data-act="select" type="button">Character Selection</button>
      <button class="gl-menu-btn danger" data-act="logout" type="button">Log Out</button>
      <div class="gl-menu-settings">
        <div class="lbl"><span>Volume</span><span data-vol-pct></span></div>
        <input type="range" min="0" max="100" step="1" data-vol />
        <label class="toggle"><span>Invert Mouse</span><input type="checkbox" data-invert /></label>
      </div>
    </div>`;

  el.addEventListener("click", (e) => {
    if (e.target === el) closeGameMenu(); // click the backdrop to dismiss
  });
  el.querySelector('[data-act="resume"]')!.addEventListener("click", () => closeGameMenu());
  el.querySelector('[data-act="select"]')!.addEventListener("click", () => {
    closeGameMenu();
    requestSelection(); // tear down the world, back to character selection
  });
  el.querySelector('[data-act="logout"]')!.addEventListener("click", () => {
    closeGameMenu();
    void session.logout().finally(() => requestSelection()); // logged out → boot flow shows auth
  });
  // Volume slider — drives the global master gain (persisted).
  const slider = el.querySelector<HTMLInputElement>("[data-vol]")!;
  const pct = el.querySelector<HTMLSpanElement>("[data-vol-pct]")!;
  const sync = (v: number): void => { pct.textContent = `${Math.round(v * 100)}%`; };
  slider.value = String(Math.round(audio.getVolume() * 100));
  sync(audio.getVolume());
  slider.addEventListener("input", () => { const v = Number(slider.value) / 100; audio.setVolume(v); sync(v); });
  // Invert-mouse toggle — flips the vertical camera look (persisted).
  const invert = el.querySelector<HTMLInputElement>("[data-invert]")!;
  invert.checked = getInvertMouse();
  invert.addEventListener("change", () => setInvertMouse(invert.checked));
  return el;
}

export function openGameMenu(): void {
  if (overlay) return;
  overlay = build();
  document.body.appendChild(overlay);
}

export function closeGameMenu(): void {
  overlay?.remove();
  overlay = null;
}

export function isGameMenuOpen(): boolean {
  return overlay !== null;
}

export function toggleGameMenu(): void {
  if (overlay) closeGameMenu();
  else openGameMenu();
}

/** Remove the menu + DOM (called when the world tears down). */
export function destroyGameMenu(): void {
  closeGameMenu();
}
